/**
 * leadsFlow.ts — the Inbound Leads tab's pure decisions (§8.F), kept out of
 * the components so vitest can pin them without DOM (the closeFlow pattern).
 *
 * Two invariants live here:
 *  - the QUEUE PREDICATE (L8-2, Klemen 2026-08-22): status 'new' AND
 *    converted=false — both server-side. Composing them here means the tab
 *    cannot drift into a client-side filter of the capped list.
 *  - the CONVERT COMPANY RULE: the server refuses with a named 400 when
 *    neither the lead nor the body carries a company. The modal catches the
 *    foreseeable case BEFORE the call — company becomes a required field
 *    when the lead has none; the 400 stays the backstop, never the
 *    user-facing path.
 */
import type { ConvertContactRequest, SalesMatchCandidate } from '../../../../api/types'

/** GET /v1/sales/contacts params for the Inbound Leads queue (L8-2):
 *  fresh (status new) AND unconverted (converted=false, the L1 rider).
 *  limit matches the house 200-cap. */
export function leadsQueueParams(): { status: 'new'; converted: 'false'; limit: 200 } {
  return { status: 'new', converted: 'false', limit: 200 }
}

/** The modal renders a company input ONLY for a lead without one — a lead
 *  that carries a company inherits it server-side (body-first-then-contact),
 *  so the modal never sends a company for it. */
export function needsCompanyField(leadCompany: string): boolean {
  return leadCompany.trim() === ''
}

export interface ConvertFormState {
  /** The lead's own company ('' when it has none). */
  leadCompany: string
  title: string
  /** The modal's company input; rendered only when needsCompanyField. */
  company: string
  /** C2.3: the CANONICAL name of an account the rep picked from the
   *  duplicate chooser, '' when none. Travels as body.company (see
   *  buildConvertBody) — C0's resolve-or-create matches it by normalised
   *  name, links the deal, creates nothing. No accountId wire field. */
  pickedCompany: string
  /** Decimal pounds as typed; '' = not supplied. */
  value: string
  expectedClose: string
  /** The selected employee's ID (E360-2 owner wire), '' = unassigned. */
  assignedEmployeeId: string
  busy: boolean
  /** C3: set ONLY by the refusal tile's "Create anyway" — the server refused
   *  the last submit as a possible duplicate and the rep, having seen the
   *  candidates, overrides. Never pre-set, never persisted across submits. */
  createAnyway?: boolean
}

/** Confirm enables only when the call cannot fail on a foreseeable empty
 *  field: title present, company present on ONE of lead/body, value (if
 *  typed) a valid non-negative number, and no submit in flight (the
 *  double-submit guard). */
export function convertConfirmable(f: ConvertFormState): boolean {
  if (f.busy) return false
  if (f.title.trim() === '') return false
  if (needsCompanyField(f.leadCompany) && f.company.trim() === '') return false
  if (f.value.trim() !== '') {
    const v = Number(f.value)
    if (!Number.isFinite(v) || v < 0) return false
  }
  return true
}

/** The wire body: trimmed, empties omitted, company included ONLY when the
 *  lead has none (body-first on the server — sending it for a lead that has
 *  one would silently override). */
export function buildConvertBody(f: ConvertFormState): ConvertContactRequest {
  const body: ConvertContactRequest = { title: f.title.trim() }
  // C2.3: a picked account WINS, and it wins even for a lead that has its own
  // company — that is deliberately the server's body-first override, because
  // the rep chose it. The canonical name resolves on the server by normalised
  // equality to exactly that account (C0), so the deal is born linked and no
  // account is created. Trade-off, recorded in §15.10.10: this relies on the
  // account not being renamed between pick and submit; if it were, the
  // benign outcome is a correctly-named NEW account, never a wrong link.
  if (f.pickedCompany.trim() !== '') body.company = f.pickedCompany.trim()
  else if (needsCompanyField(f.leadCompany) && f.company.trim() !== '') body.company = f.company.trim()
  if (f.value.trim() !== '' && Number.isFinite(Number(f.value))) body.value = Number(f.value)
  if (f.expectedClose !== '') body.expectedClose = new Date(`${f.expectedClose}T00:00:00Z`).toISOString()
  // E360-2: the owner wire is the employee ID (the server refuses a name).
  if (f.assignedEmployeeId.trim() !== '') body.assignedEmployeeId = f.assignedEmployeeId.trim()
  // C3: the override travels only when true — an absent key is the normal
  // submit, so a server that predates the refusal sees the C2 body unchanged.
  if (f.createAnyway === true) body.createAnyway = true
  return body
}

// ── C2 — the duplicate chooser's pure parts ────────────────────────────────

/** What the pre-flight asks about. C2.2: the lead's own email + company and
 *  the lead itself excluded; when the Company field is rendered (lead has
 *  none) the TYPED company is what gets matched, re-queried on blur. */
export function matchesQueryFor(lead: { id: string; email: string; company: string }, typedCompany: string): {
  email: string; company: string; excludeContactId: string
} {
  const company = needsCompanyField(lead.company) ? typedCompany.trim() : lead.company.trim()
  return { email: lead.email.trim(), company, excludeContactId: lead.id }
}

/** C1.1's render rule as a pure function: canonical name primary when a link
 *  exists, the raw text beneath ONLY when it differs, raw alone when unlinked.
 *  "Differs" is EXACT text (trimmed): "acme saas" under "Acme SaaS" is shown,
 *  because a casing difference is precisely the two-spellings problem the
 *  canonical name exists to expose — hiding it would defeat the rule. For an
 *  account candidate the two are one string, so secondary is ''. */
export function candidateLabel(c: SalesMatchCandidate): { primary: string; secondary: string } {
  const raw = (c.company ?? '').trim()
  const canonical = (c.accountName ?? '').trim()
  if (c.type === 'account') return { primary: c.displayName, secondary: '' }
  if (canonical !== '') {
    return { primary: canonical, secondary: raw !== '' && raw !== canonical ? raw : '' }
  }
  return { primary: raw, secondary: '' }
}

/** WHY it matched, in words — never a bare list (C2.2). */
export function ruleLabel(c: SalesMatchCandidate): string {
  switch (c.rule) {
    case 'email_exact': return 'Same email'
    case 'company_trigram': return `Similar company · ${Math.round(c.score * 100)}% match`
    default: return String(c.rule)
  }
}
